import { z } from "zod";
import { localStore, type Store } from "../../lib/store";
import { apiClient } from "../../services/api";
import { ClientError } from "../../../../api/src/shared/request";
import {
  openNativeAuthSession,
  type NativeAuthSessionRequest,
} from "../../tools/native-auth-session";

// OAuth user info type - varies by provider (validated as object)
const OAuthUserInfoSchema = z.record(z.string(), z.unknown());
export type OAuthUserInfo = z.infer<typeof OAuthUserInfoSchema>;

export interface OAuthConfig {
  name: string;
  clientId: string;
  authorizeUrl: string;
  /** Where a lapsed member renews. Opened outside the app, never in place. */
  renewUrl: string;
  scope: string;
  storagePrefix: string;
  parseTokenResponse?: (params: URLSearchParams) => TokenData | null;
  /** The provider's access rule, run on every fresh or cached user info. */
  canOpenRoom: (userInfo: OAuthUserInfo) => OAuthResult;
}

/** How {@link OAuthManager.startAuth} ended. */
export type AuthStart =
  /** The page is navigating to the authorization server. Nothing else to do. */
  | { kind: "redirecting" }
  /** The native sheet came back. Navigate the app to `target` to finish. */
  | { kind: "callback"; target: string }
  /** The reader closed the sheet. Say nothing; leave the screen as it is. */
  | { kind: "cancelled" }
  /** The sheet ended on a fault. `reason` is for logs, not for the reader. */
  | { kind: "failed"; reason: string };

export interface TokenData {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number; // en secondes
  expiryDate?: Date;
  user?: OAuthUserInfo;
}

export interface OAuthResult {
  success: boolean;
  user?: OAuthUserInfo;
  error?: string;
  errorCode?:
    | "TOKEN_EXPIRED"
    | "MEMBERSHIP_INACTIVE"
    | "NOT_AUTHENTICATED"
    | "ALREADY_PROCESSING";
}

// HACK (SFCTCV): a successful OAuth grants this much access WITHOUT blocking
// the reader on the provider — but only while the membership snapshot stays
// active. A cached-inactive user falls through to a live check, so a renewal
// is still picked up. Matches the "force reconnection after one month"
// behaviour the partner signed off on: connected once === connected for a
// month, for members. `renewInBackground` rolls that month forward for a
// reader who keeps coming, off the render path.
const AUTH_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// Refresh the access token once it is this close to expiry. SFCTCV issues
// access tokens that live about an hour, so an active reader costs at most one
// refresh per hour, and any reader who comes back after an hour away rolls the
// whole token chain forward on arrival.
const RENEW_AHEAD_MS = 5 * 60 * 1000;

const OAuthTokenStateSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string().nullable(),
  tokenExpiryMs: z.number(),
  user: OAuthUserInfoSchema.nullable(),
  // Timestamp (ms) of the OAuth that opened the 30-day access window. Defaults
  // to null so states persisted before this field existed still parse instead
  // of being wiped — they get stamped "now" on first read (see
  // resolveAuthWindowStart), granting already-connected users a fresh 30 days
  // from deploy rather than logging them out.
  authedAtMs: z.number().nullable().default(null),
});

type OAuthTokenState = z.infer<typeof OAuthTokenStateSchema>;

export class OAuthManager {
  private config: OAuthConfig;
  private tokenStore: Store<OAuthTokenState | null>;
  private csrfStateStore: Store<string | null>;
  private isHandlingCallback = false; // singleton
  // Dedupe concurrent revalidations (mount + visibilitychange firing together
  // would otherwise hit SFCTCV twice). Cleared once the promise settles.
  private inflightRevalidation: Promise<OAuthResult> | null = null;
  // Same reason, for the background token renewal: a mount and a
  // visibilitychange landing together must cost SFCTCV one refresh, not two.
  private inflightRenewal: Promise<void> | null = null;

  constructor(config: OAuthConfig) {
    this.config = config;
    this.tokenStore = localStore<OAuthTokenState | null>(
      `${config.storagePrefix}_oauth_v2`,
      null,
      OAuthTokenStateSchema.nullable()
    );
    this.csrfStateStore = localStore<string | null>(
      `${config.storagePrefix}_oauth_state`,
      null,
      z.string().nullable()
    );
  }

  /** Where a lapsed member renews, read straight off the provider's config. */
  get renewUrl(): string {
    return this.config.renewUrl;
  }

  /** Expiry timestamp in ms, or null if never authenticated. */
  getTokenExpiryMs(): number | null {
    const state = this.tokenStore.get();
    return state === null ? null : state.tokenExpiryMs;
  }

  /**
   * Start of the 30-day access window. States persisted before `authedAtMs`
   * existed (null) are stamped "now" on first read, so users already connected
   * at deploy time get a fresh 30 days from their first post-deploy visit
   * instead of being logged out by the new gate.
   */
  private resolveAuthWindowStart(state: OAuthTokenState): number {
    if (state.authedAtMs !== null) return state.authedAtMs;
    const now = Date.now();
    this.tokenStore.set({ ...state, authedAtMs: now });
    return now;
  }

  private isWithinAuthWindow(state: OAuthTokenState): boolean {
    return Date.now() - this.resolveAuthWindowStart(state) < AUTH_WINDOW_MS;
  }

  /**
   * Génère un état aléatoire pour la protection CSRF
   */
  private generateState(): string {
    return (
      Math.random().toString(36).slice(2, 15) +
      Math.random().toString(36).slice(2, 15)
    );
  }

  /**
   * Stocke les tokens. tokenExpiryMs reflète l'expiry réel renvoyé par le
   * serveur OAuth — pas une fenêtre artificielle. Quand il expire on appelle
   * /refresh (cf. revalidateUserInfo).
   */
  private storeTokens(tokenData: TokenData): void {
    // expiresIn est en secondes. Fallback 1h si le serveur ne le donne pas.
    const expiresInMs =
      tokenData.expiresIn === undefined || tokenData.expiresIn === 0
        ? 60 * 60 * 1000
        : tokenData.expiresIn * 1000;
    const tokenExpiryMs = Date.now() + expiresInMs;

    this.tokenStore.set((prev) => ({
      accessToken: tokenData.accessToken,
      refreshToken:
        tokenData.refreshToken === undefined ? null : tokenData.refreshToken,
      tokenExpiryMs,
      // A token write means the user just (re-)connected — (re)open the 30-day
      // window from now.
      authedAtMs: Date.now(),
      // Preserve user from a prior exchange if this storeTokens call doesn't carry one.
      user:
        tokenData.user === undefined
          ? prev === null
            ? null
            : prev.user
          : tokenData.user,
    }));
  }

  /**
   * Vérifie si le token est valide
   */
  isAuthenticated(): boolean {
    const state = this.tokenStore.get();
    if (state === null) return false;
    return Date.now() < state.tokenExpiryMs;
  }

  /**
   * Récupère le token stocké
   */
  getAccessToken(): string | null {
    if (!this.isAuthenticated()) {
      return null;
    }
    const state = this.tokenStore.get();
    return state === null ? null : state.accessToken;
  }

  /**
   * Récupère l'utilisateur stocké
   */
  getUser(): OAuthUserInfo | null {
    const state = this.tokenStore.get();
    return state === null ? null : state.user;
  }

  /**
   * Starts the OAuth flow and says how it ended.
   *
   * On a phone the partner page opens in a sheet the native shell presents:
   * the app stays alive behind it, the sheet insets its own content, and its
   * close control always works — so a partner outage no longer traps the
   * reader on a dead page. The sheet hands the callback URL back and the
   * caller navigates to it inside the app.
   *
   * Everywhere else, and in shells built before that bridge, the page
   * redirects to the authorization server as it always did.
   */
  async startAuth(
    redirectUri: string,
    labels: NativeAuthSessionRequest["labels"]
  ): Promise<AuthStart> {
    const state = this.generateState();
    this.csrfStateStore.set(state);

    // Le state est uniquement dans les params OAuth, pas dans le redirect_uri
    // Le serveur OAuth le renverra automatiquement dans l'URL de callback
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: redirectUri,
      scope: this.config.scope,
      response_type: "code",
      state: state,
    });

    const authUrl = `${this.config.authorizeUrl}?${params.toString()}`;

    const outcome = await openNativeAuthSession({
      url: authUrl,
      redirectUri,
      labels,
    });

    switch (outcome.status) {
      case "unsupported": {
        globalThis.location.href = authUrl;
        return { kind: "redirecting" };
      }
      case "success": {
        // Same origin as the app, so this stays an in-app route change: the
        // callback page reads the query and runs the exchange. The CSRF state
        // stays in store — handleCallback is the one that consumes it.
        const callbackUrl = new URL(outcome.url);
        return {
          kind: "callback",
          target: `${callbackUrl.pathname}${callbackUrl.search}`,
        };
      }
      case "cancelled": {
        this.csrfStateStore.set(null);
        return { kind: "cancelled" };
      }
      case "failed": {
        this.csrfStateStore.set(null);
        return { kind: "failed", reason: outcome.reason };
      }
      default:
        throw new Error(outcome satisfies never);
    }
  }

  /**
   * Gère le callback OAuth (à appeler sur la page de redirect)
   */
  async handleCallback(redirectUri: string): Promise<OAuthResult> {
    if (this.isHandlingCallback) {
      console.log("Already handling callback, skipping");
      return {
        success: false,
        error: "Already processing",
        errorCode: "ALREADY_PROCESSING",
      };
    }

    this.isHandlingCallback = true;

    // Everything below is inside the try, so that `finally` releases the flag
    // on EVERY exit. The two checks that follow used to return above it, which
    // left the flag set for good. That was invisible while each attempt was a
    // fresh page load and the manager was rebuilt with it. The sheet ends in
    // `history.replace`, so the manager now outlives the attempt, and one
    // partner error or one stale state made every later callback answer
    // ALREADY_PROCESSING. The callback page returns on that without marking
    // itself handled, so the reader stays on the spinner until the app dies.
    try {
      const urlParams = new URLSearchParams(globalThis.location.search);

      // Vérifier si erreur
      const error = urlParams.get("error");
      if (error) {
        OAuthManager.cleanUrlParams();
        return {
          success: false,
          error: `OAuth error: ${error} - ${urlParams.get("error_description") || ""}`,
          errorCode: "NOT_AUTHENTICATED",
        };
      }

      // Vérifier le state (CSRF protection)
      const state = urlParams.get("state");
      const storedState = this.csrfStateStore.get();

      if (!state || state !== storedState) {
        OAuthManager.cleanUrlParams();
        return {
          success: false,
          error: "Invalid state parameter. Possible CSRF attack.",
          errorCode: "NOT_AUTHENTICATED",
        };
      }

      let tokenData: TokenData;

      // Cas 1 : On reçoit directement un access_token (implicit flow)
      const accessToken = urlParams.get("access_token");
      if (accessToken) {
        tokenData = this.parseImplicitFlowResponse(urlParams);
      }
      // Cas 2 : On reçoit un code à échanger (authorization code flow)
      else {
        const code = urlParams.get("code");
        if (!code) {
          return {
            success: false,
            error: "No authorization code or access token received",
            errorCode: "NOT_AUTHENTICATED",
          };
        }
        // Le redirect_uri doit être identique à celui de l'init (sans params)
        const url = new URL(redirectUri, globalThis.location.origin);
        const cleanUrl = url.origin + url.pathname;
        const result = await this.exchangeCodeForToken(code, cleanUrl);
        if (result === null) {
          return {
            success: false,
            error: "Token exchange failed",
            errorCode: "NOT_AUTHENTICATED",
          };
        }
        tokenData = result;
      }

      // Stocker les tokens
      this.storeTokens(tokenData);

      // Nettoyer
      OAuthManager.cleanUrlParams();

      return {
        success: true,
        user: tokenData.user,
      };
    } catch (error) {
      this.clearTokens();
      OAuthManager.cleanUrlParams();
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
        errorCode: "NOT_AUTHENTICATED",
      };
    } finally {
      this.isHandlingCallback = false;
      this.csrfStateStore.set(null);
    }
  }

  /**
   * Parse la réponse d'un implicit flow (token dans l'URL)
   */
  private parseImplicitFlowResponse(params: URLSearchParams): TokenData {
    if (this.config.parseTokenResponse) {
      const parsed = this.config.parseTokenResponse(params);
      if (!parsed) {
        throw new Error("Failed to parse token response");
      }
      return parsed;
    }

    const accessToken = params.get("access_token");
    if (!accessToken) {
      throw new Error("No access token in response");
    }

    const expiresIn = params.get("expires_in");

    return {
      accessToken,
      expiresIn: expiresIn ? Number.parseInt(expiresIn, 10) : undefined,
    };
  }

  /**
   * Échange le code d'autorisation contre un token
   */
  private async exchangeCodeForToken(
    code: string,
    redirectUri: string
  ): Promise<TokenData | null> {
    const params = {
      name: this.config.name,
      redirect_uri: redirectUri,
      code: code,
    };

    try {
      const { body: data } = await apiClient.post(
        "/webapp/public/oauth/external/token",
        { json: params }
      );

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        user: data.user,
      };
    } catch {
      // api error already captured by onRequestError; caller checks for null
      // and shows its own user-facing feedback.
      return null;
    }
  }

  /**
   * Synchronous access check based on the locally cached user info. Cheap to
   * call from render paths (menu badges, etc.). For correctness on the room
   * entry path, use {@link revalidateUserInfo} which hits the OAuth server to
   * pick up membership changes (renewals, revocations).
   */
  canOpenRoom(): OAuthResult {
    const state = this.tokenStore.get();
    if (state === null || state.user === null) {
      return {
        success: false,
        error: "User not authenticated",
        errorCode: "NOT_AUTHENTICATED",
      };
    }
    // HACK: connected once === connected for a month, but only for members.
    // Within the 30-day window we gate on the cached membership snapshot (no
    // SFCTCV round-trip); outside it, force a fresh re-OAuth.
    if (!this.isWithinAuthWindow(state)) {
      return {
        success: false,
        error: "Token expired",
        errorCode: "TOKEN_EXPIRED",
      };
    }
    return this.config.canOpenRoom(state.user);
  }

  /**
   * Force a fresh check against the OAuth server: calls /userinfo to re-read
   * membership state, transparently refreshing the access token via /refresh
   * on 401. Updates the local cache with whatever comes back, then runs the
   * provider's `canOpenRoom` predicate on the fresh user info.
   *
   * This is the ONLY path that picks up server-side changes (membership
   * renewed/revoked, account closed, etc.). Call it on room entry and after
   * the user returns from the renewal URL.
   *
   * Concurrent callers share the same in-flight promise to avoid hammering
   * the OAuth server on mount + visibilitychange overlap.
   */
  revalidateUserInfo(): Promise<OAuthResult> {
    // HACK: connected once === connected for a month, but only for members.
    // If the cached membership snapshot is active and we're within the 30-day
    // window, grant access without making the reader wait on SFCTCV. A
    // cached-inactive user (or a lapsed window) falls through to the live
    // check below, so a member who renews is still picked up.
    const cached = this.tokenStore.get();
    if (
      cached !== null &&
      cached.user !== null &&
      this.isWithinAuthWindow(cached)
    ) {
      const cachedResult = this.config.canOpenRoom(cached.user);
      if (cachedResult.success) {
        // Off the critical path: the reader is already in. See
        // `renewInBackground` for why this runs at all.
        void this.renewInBackground();
        return Promise.resolve(cachedResult);
      }
    }
    if (this.inflightRevalidation !== null) return this.inflightRevalidation;
    const promise = this.runRevalidation().finally(() => {
      this.inflightRevalidation = null;
    });
    this.inflightRevalidation = promise;
    return promise;
  }

  /**
   * Rolls the provider's tokens forward before they die, in the background.
   *
   * SFCTCV's access token lives about an hour and its refresh token a few
   * weeks from the moment it was issued. Reaching for the refresh token only
   * after a 401 means reaching for it weeks later, when it has expired too:
   * production shows exactly that, `invalid_grant` with "Refresh token has
   * expired", and the reader pays for it with a login well inside the month
   * the partner agreed to (Sentry JUISCI-API2-HD / JUISCI-APP-29K, 32 readers).
   * Using the refresh token while it is alive keeps the chain alive, because
   * the provider rotates it on every use.
   *
   * Each refresh also re-reads membership at SFCTCV, so a reader who comes
   * back regularly costs no login AND carries a subscription state no older
   * than their last visit, rather than a snapshot up to a month old.
   *
   * Fire and forget. Nothing on screen waits for it, and every failure is
   * silent by design: the cached snapshot has already granted access, and the
   * next lapsed window sends the reader through SFCTCV once.
   */
  private renewInBackground(): Promise<void> {
    if (this.inflightRenewal !== null) return this.inflightRenewal;

    const state = this.tokenStore.get();
    if (state === null) return Promise.resolve();
    const refreshToken = state.refreshToken;
    if (refreshToken === null) return Promise.resolve();
    if (Date.now() < state.tokenExpiryMs - RENEW_AHEAD_MS)
      return Promise.resolve();

    const promise = this.refreshAccessToken(refreshToken)
      .then((refreshed) => {
        // The store can have moved on while this was in flight: the reader
        // logged out, or started a new authorization, and either replaced the
        // credential this call presented. Writing the answer to a question
        // nobody is asking any more would resurrect what `clearTokens` removed,
        // with a fresh 30-day window stamped on top, or overwrite a newer
        // credential with an older one. So the write only lands while the
        // stored refresh token is still the one that was sent.
        const current = this.tokenStore.get();
        if (current === null || current.refreshToken !== refreshToken) return;

        switch (refreshed.kind) {
          case "ok":
            // storeTokens re-stamps authedAtMs, which is the point: a live
            // membership read from SFCTCV renews the month it opens.
            this.storeTokens(refreshed.tokens);
            return;
          case "unauthorized":
            // The refresh token is dead, so stop presenting it. Without this
            // the token stays stored, `tokenExpiryMs` stays in the past, and
            // every later room entry spends another rejected /refresh on the
            // provider for the rest of the 30-day window. The access window
            // still carries the reader; runRevalidation owns the wipe.
            this.tokenStore.set((prev) =>
              prev === null ? null : { ...prev, refreshToken: null }
            );
            return;
          case "transient":
            return;
          default:
            throw new Error(refreshed satisfies never);
        }
      })
      .catch((error: unknown) => {
        // storeTokens writes to localStorage, which throws on a full quota and
        // in Safari private mode. Nothing on screen waited for this, so the
        // reader must not learn about it through an unhandled rejection.
        console.warn(
          `partner token renewal failed: ${error instanceof Error ? error.message : String(error)}`
        );
      })
      .finally(() => {
        this.inflightRenewal = null;
      });

    this.inflightRenewal = promise;
    return promise;
  }

  private async runRevalidation(): Promise<OAuthResult> {
    const state = this.tokenStore.get();
    if (state === null) {
      return {
        success: false,
        error: "User not authenticated",
        errorCode: "NOT_AUTHENTICATED",
      };
    }

    const first = await this.fetchUserInfo(state.accessToken);

    if (first.kind === "ok") {
      this.tokenStore.set({ ...state, user: first.user });
      return this.config.canOpenRoom(first.user);
    }

    if (first.kind === "unauthorized" && state.refreshToken !== null) {
      const refreshed = await this.refreshAccessToken(state.refreshToken);
      if (refreshed.kind === "ok") {
        this.storeTokens(refreshed.tokens);
        const freshUser = refreshed.tokens.user;
        if (freshUser === undefined) {
          // Server should always return a user on refresh; treat as a soft
          // failure rather than silently passing through cached state.
          return {
            success: false,
            error: "Refresh returned no user",
            errorCode: "NOT_AUTHENTICATED",
          };
        }
        return this.config.canOpenRoom(freshUser);
      }
      if (refreshed.kind === "unauthorized") {
        // Refresh token explicitly rejected — wipe so the user can re-OAuth
        // from a clean slate.
        this.clearTokens();
        return {
          success: false,
          error: "Token expired",
          errorCode: "TOKEN_EXPIRED",
        };
      }
      // Refresh failed transiently (network, SFCTCV 5xx). Keep the cached
      // state intact and fall through to the cached-fallback branch below so
      // the next attempt can retry.
    }

    if (first.kind === "unauthorized" && state.refreshToken === null) {
      // No refresh token to fall back on.
      this.clearTokens();
      return {
        success: false,
        error: "Token expired",
        errorCode: "TOKEN_EXPIRED",
      };
    }

    // Transient network/server error (from /userinfo or /refresh) — fall back
    // to the last cached state so a flaky SFCTCV or offline phone doesn't
    // lock the user out unnecessarily.
    if (state.user === null) {
      return {
        success: false,
        error: "User not authenticated",
        errorCode: "NOT_AUTHENTICATED",
      };
    }
    return this.config.canOpenRoom(state.user);
  }

  private async fetchUserInfo(
    accessToken: string
  ): Promise<
    | { kind: "ok"; user: OAuthUserInfo }
    | { kind: "unauthorized" }
    | { kind: "transient" }
  > {
    try {
      const { body } = await apiClient.post(
        "/webapp/public/oauth/external/userinfo",
        { json: { name: this.config.name, access_token: accessToken } }
      );
      return { kind: "ok", user: body.user };
    } catch (error) {
      if (error instanceof ClientError && error.responseStatus === 401) {
        return { kind: "unauthorized" };
      }
      return { kind: "transient" };
    }
  }

  private async refreshAccessToken(
    refreshToken: string
  ): Promise<
    | { kind: "ok"; tokens: TokenData }
    | { kind: "unauthorized" }
    | { kind: "transient" }
  > {
    try {
      const { body } = await apiClient.post(
        "/webapp/public/oauth/external/refresh",
        { json: { name: this.config.name, refresh_token: refreshToken } }
      );
      return {
        kind: "ok",
        tokens: {
          accessToken: body.access_token,
          refreshToken: body.refresh_token,
          expiresIn: body.expires_in,
          user: body.user,
        },
      };
    } catch (error) {
      // Distinguish a confirmed refresh-token rejection from a transient
      // failure (network blip, SFCTCV 5xx). Only the former should wipe the
      // user's stored credentials; the latter must preserve them so the next
      // attempt can recover without forcing a full re-OAuth.
      if (error instanceof ClientError && error.responseStatus === 401) {
        return { kind: "unauthorized" };
      }
      return { kind: "transient" };
    }
  }

  /**
   * Déconnexion - nettoie tous les tokens
   */
  logout(): void {
    this.clearTokens();
  }

  /**
   * Clear all persisted OAuth state for this provider.
   */
  private clearTokens(): void {
    this.tokenStore.set(null);
    this.csrfStateStore.set(null);
  }

  /**
   * Nettoie les paramètres OAuth de l'URL
   */
  static cleanUrlParams(): void {
    const url = new URL(globalThis.location.href);
    const paramsToRemove = [
      "code",
      "access_token",
      "token_type",
      "expires_in",
      "error",
      "error_description",
    ];

    for (const param of paramsToRemove) url.searchParams.delete(param);

    globalThis.history.replaceState({}, "", url.toString());
  }
}

// Helper pour créer un manager depuis la config
export function createOAuthManager(config: OAuthConfig): OAuthManager {
  return new OAuthManager(config);
}
